import { AxiosResponse } from "axios";
import { API } from "../index";

type BooleanQueryParam = boolean | "true" | "false";

export type VisibilityTarget = "web" | "pos";

export type MenuCategoryIconKey =
  | "all"
  | "cake"
  | "chicken"
  | "coffee"
  | "cup"
  | "dessert"
  | "drink"
  | "flame"
  | "leaf"
  | "meat"
  | "pizza"
  | "side"
  | "utensils";

export type MenuCategory = {
  id: number;
  site: number;
  name: string;
  icon_key?: MenuCategoryIconKey | null;
  display_order: number;
  is_active: boolean;
  is_web_visible: boolean;
  is_pos_visible: boolean;
};

export type MenuOption = {
  id: number;
  option_group: number;
  name: string;
  extra_price: number;
  display_order: number;
  is_active: boolean;
};

export type MenuOptionGroup = {
  id: number;
  menu: number;
  name: string;
  is_required: boolean;
  min_select: number;
  max_select: number;
  display_order: number;
  is_active: boolean;
  options: MenuOption[];
};

export type Menu = {
  id: number;
  site: number;
  category: number;
  name: string;
  description: string;
  price: number;
  is_sold_out: boolean;
  display_order: number;
  is_active: boolean;
  is_web_visible: boolean;
  is_pos_visible: boolean;
  allow_pos_price_override: boolean;
  fulfillment_type: "INSTANT" | "KITCHEN";
  image?: string | null;
  option_groups: MenuOptionGroup[];
};

export type MenuCategoryListParams = {
  is_active?: BooleanQueryParam;
  visibility_target?: VisibilityTarget;
  page?: number;
  page_size?: number;
};

export type MenuListParams = {
  category_id?: number;
  is_active?: BooleanQueryParam;
  include_sold_out?: BooleanQueryParam;
  visibility_target?: VisibilityTarget;
  page?: number;
  page_size?: number;
};

export type TableGroup = {
  id: number;
  site: number;
  name: string;
  display_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
};

export type ServiceTable = {
  id: number;
  site: number;
  group: number | null;
  group_name: string | null;
  code: string;
  name: string;
  capacity: number;
  display_order: number;
  is_active: boolean;
};

export type DraftOptionPayload = {
  menu_option_id: number;
  quantity: number;
};

export type OrderItemPayload = {
  menu_id: number;
  quantity: number;
  options: DraftOptionPayload[];
};

export type DraftPayload = {
  items: OrderItemPayload[];
};

export type SubmitTableOrderPayload = {
  table_id?: number | null;
  memo?: string;
  order_source?: TableOrderSource;
  order_type: "serving" | "pickup";
  items?: OrderItemPayload[];
  draft_key?: string;
};

export type DraftResponseOption = {
  id: number;
  menu_option_id: number | null;
  option_name_snapshot: string;
  extra_price_snapshot: number;
  quantity: number;
};

export type DraftResponseItem = {
  id: number;
  menu_id: number;
  menu_name_snapshot: string;
  unit_price_snapshot: number;
  quantity: number;
  line_amount: number;
  options: DraftResponseOption[];
};

export type DraftResponse = {
  table_id: number;
  draft_key: string | null;
  items: DraftResponseItem[];
  total_amount: number;
};

export type CustomerRegistrationState = "NONE" | "EXISTING_USER" | "AUTO_CREATED_USER";
export type CustomerPhoneCaptureMode = "CRM_DIRECT" | "TOSS_FRONT" | "SKIP";
export type CustomerPhoneCaptureDetail = "NONE" | "MANUAL" | "KEYPAD" | "QR";
export type TableOrderSource = "WEB_CUSTOMER" | "CRM_POS" | "UNKNOWN";

export type TableOrderItemOption = {
  id: number;
  menu_option_id: number | null;
  option_name_snapshot: string;
  extra_price_snapshot: number;
  quantity: number;
};

export type TableOrderItem = {
  id: number;
  menu_id: number;
  menu_name_snapshot: string;
  unit_price_snapshot: number;
  quantity: number;
  line_amount: number;
  options: TableOrderItemOption[];
};

export type TableOrder = {
  id: number;
  site: number;
  table: number | null;
  table_name: string | null;
  table_code: string | null;
  created_by: number;
  created_by_phone: string;
  customer_user: number | null;
  customer_phone_num: string;
  customer_name: string;
  customer_registration_state: CustomerRegistrationState;
  customer_phone_capture_mode: CustomerPhoneCaptureMode;
  customer_phone_capture_detail: CustomerPhoneCaptureDetail;
  customer_phone_captured_at: string | null;
  order: number | null;
  order_source: TableOrderSource;
  order_type: "serving" | "pickup";
  fulfillment_type: "INSTANT" | "KITCHEN" | null;
  status: "NEW" | "ACCEPTED" | "SERVED" | "CANCELED";
  payment_status:
    | "UNPAID"
    | "PAYMENT_PENDING"
    | "PAID"
    | "PAYMENT_FAILED"
    | "CANCEL_PENDING"
    | "CANCELED";
  memo: string;
  total_amount: number;
  payment_requested_at: string | null;
  paid_at: string | null;
  payment_failed_at: string | null;
  payment_failure_reason: string;
  canceled_payment_at: string | null;
  accepted_at: string | null;
  served_at: string | null;
  canceled_at: string | null;
  created_at: string;
  updated_at: string;
  items: TableOrderItem[];
};

export type PaginatedResponse<T> = {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
};

const BASE = "/api/v2";
const MAX_PAGE_SIZE = 100;

const fetchAllPages = async <T>(
  path: string,
  params?: Record<string, any>
): Promise<PaginatedResponse<T>> => {
  let nextUrl: string | null = null;
  let page = 1;
  const results: T[] = [];

  while (true) {
    let response: AxiosResponse<PaginatedResponse<T>>;
    if (nextUrl) {
      response = await API.get(nextUrl);
    } else {
      const requestParams = { ...(params || {}), page, page_size: MAX_PAGE_SIZE };
      response = await API.get(path, { params: requestParams });
    }

    const data = response.data;
    results.push(...(data.results || []));

    if (!data.next) break;
    nextUrl = data.next;
    page += 1;
  }

  return {
    count: results.length,
    next: null,
    previous: null,
    results,
  };
};

export const tableOrderService = {
  listCategories: (params?: MenuCategoryListParams): Promise<AxiosResponse<PaginatedResponse<MenuCategory>>> =>
    API.get(`${BASE}/menu-categories/`, { params }),
  listAllCategories: (params?: MenuCategoryListParams): Promise<PaginatedResponse<MenuCategory>> =>
    fetchAllPages<MenuCategory>(`${BASE}/menu-categories/`, params),
  listMenus: (params?: MenuListParams): Promise<AxiosResponse<PaginatedResponse<Menu>>> =>
    API.get(`${BASE}/menus/`, {
      params: {
        include_sold_out: true,
        ...(params || {}),
      },
    }),
  listAllMenus: (params?: MenuListParams): Promise<PaginatedResponse<Menu>> =>
    fetchAllPages<Menu>(`${BASE}/menus/`, {
      include_sold_out: true,
      ...(params || {}),
    }),
  listTableGroups: (params?: Record<string, any>): Promise<AxiosResponse<PaginatedResponse<TableGroup>>> =>
    API.get(`${BASE}/table-groups/`, { params: { is_active: true, ...(params || {}) } }),
  listAllTableGroups: (params?: Record<string, any>): Promise<PaginatedResponse<TableGroup>> =>
    fetchAllPages<TableGroup>(`${BASE}/table-groups/`, { is_active: true, ...(params || {}) }),
  listServiceTables: (params?: Record<string, any>): Promise<AxiosResponse<PaginatedResponse<ServiceTable>>> =>
    API.get(`${BASE}/service-tables/`, { params: { is_active: true, ...(params || {}) } }),
  listAllServiceTables: (params?: Record<string, any>): Promise<PaginatedResponse<ServiceTable>> =>
    fetchAllPages<ServiceTable>(`${BASE}/service-tables/`, { is_active: true, ...(params || {}) }),

  getDraft: (tableId: number): Promise<AxiosResponse<DraftResponse>> => API.get(`${BASE}/table-orders/drafts/${tableId}/`),
  putDraft: (tableId: number, payload: DraftPayload): Promise<AxiosResponse<DraftResponse>> =>
    API.put(`${BASE}/table-orders/drafts/${tableId}/`, payload),
  deleteDraft: (tableId: number): Promise<AxiosResponse<{ deleted: number }>> => API.delete(`${BASE}/table-orders/drafts/${tableId}/`),
  submitOrder: (payload: SubmitTableOrderPayload): Promise<AxiosResponse<TableOrder>> =>
    API.post(`${BASE}/table-orders/submit/`, payload),
  submitDraft: (tableId: number, memo = "", orderType: "serving" | "pickup" = "serving"): Promise<AxiosResponse<TableOrder>> =>
    API.post(`${BASE}/table-orders/submit/`, { table_id: tableId, memo, order_type: orderType }),

  listOrders: (params?: Record<string, any>): Promise<AxiosResponse<PaginatedResponse<TableOrder>>> =>
    API.get(`${BASE}/table-orders/`, { params }),
  listMyOrders: (params?: Record<string, any>): Promise<AxiosResponse<PaginatedResponse<TableOrder>>> =>
    API.get(`${BASE}/table-orders/my-orders/`, { params }),
  getMyOrderDetail: (id: number): Promise<AxiosResponse<TableOrder>> =>
    API.get(`${BASE}/table-orders/my-orders/${id}/`),
};
